'use client';

import React from 'react';

import { cn } from '../../../../lib/utils';
import { KIND_ICON } from '../../locationLibrary/format';

/** The canonical 3-value delivery kind an activity (or one of its locations) offers. */
export type LocationKind = 'venue' | 'at_home' | 'online';

/** Stable display order so a mixed card always reads venue, at-home, then online. */
const KIND_ORDER: readonly LocationKind[] = ['venue', 'at_home', 'online'];

export interface LocationKindChipsLabels {
  venue: string;
  at_home: string;
  online: string;
}

export interface LocationKindChipsProps {
  /** The distinct kinds the activity offers (deduped server-side). */
  kinds: LocationKind[];
  labels: LocationKindChipsLabels;
  /**
   * When a single kind is offered, the chip text. Lets the venue chip show a
   * city instead of the generic "At venue" label, matching the legacy single
   * indicator. Ignored when 2+ kinds are present (those read as compact chips).
   */
  singleVenueLabel?: string;
  /** Icon + text size. `sm` matches the search card; `xs` the featured card. */
  size?: 'xs' | 'sm';
  className?: string;
}

const TONE: Record<LocationKind, { icon: string; text: string }> = {
  venue: { icon: 'text-neutral-600', text: 'text-neutral-600' },
  at_home: { icon: 'text-neutral-600', text: 'text-neutral-600' },
  online: { icon: 'text-violet-600', text: 'text-violet-700' }
};

/**
 * Renders the activity's real delivery-mode MIX as small chips. A single-kind
 * activity keeps the legacy look: one icon + label row (the venue chip can show
 * a city via `singleVenueLabel`). A mixed activity (2+ kinds) lays out one
 * compact icon+label chip per kind, in a stable venue → at-home → online order.
 *
 * Pure / presentational: every string arrives via `labels`. Reuses the same
 * lucide icons as the location editor so discovery surfaces and the editor can
 * never disagree on what venue / at-home / online looks like.
 */
export const LocationKindChips: React.FC<LocationKindChipsProps> = ({
  kinds,
  labels,
  singleVenueLabel,
  size = 'sm',
  className
}) => {
  const ordered = KIND_ORDER.filter((k) => kinds.includes(k));
  // Default to a venue chip when the activity has no resolved kinds, so the row
  // never collapses to nothing (mirrors the legacy MapPin fallback).
  const effective = ordered.length > 0 ? ordered : (['venue'] as LocationKind[]);

  const iconSize = 'w-4 h-4';
  const textSize = size === 'xs' ? 'text-xs' : 'text-xs md:text-sm';

  const labelFor = (k: LocationKind): string => (k === 'venue' && singleVenueLabel ? singleVenueLabel : labels[k]);

  if (effective.length === 1) {
    const k = effective[0];
    const Icon = KIND_ICON[k];
    const tone = TONE[k];
    return (
      <div className={cn('flex items-center gap-1 min-w-0', className)}>
        <Icon className={cn(iconSize, 'flex-shrink-0', tone.icon)} aria-hidden />
        <span className={cn(textSize, 'font-medium truncate', tone.text)}>{labelFor(k)}</span>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0', className)}>
      {effective.map((k) => {
        const Icon = KIND_ICON[k];
        const tone = TONE[k];
        return (
          <span key={k} className="inline-flex items-center gap-1 min-w-0">
            <Icon className={cn(iconSize, 'flex-shrink-0', tone.icon)} aria-hidden />
            <span className={cn(textSize, 'font-medium truncate', tone.text)}>{labels[k]}</span>
          </span>
        );
      })}
    </div>
  );
};

LocationKindChips.displayName = 'LocationKindChips';
